/**
 * Wire types for travel ball player and parent/guardian accounts. Mirrors
 * `api/RosterPilotAPI/DTOs/TravelBallFamilyDtos.cs`.
 */

import type { FamilyAnnouncement } from './travelBallAnnouncements';

/** The family role a travel ball account holds. The UI says "Parent/Guardian" for `guardian`. */
export type TravelBallFamilyRole = 'player' | 'guardian';

export const isTravelBallFamilyRole = (value: unknown): value is TravelBallFamilyRole =>
  value === 'player' || value === 'guardian';

/** `POST /api/public/travel-ball/signup/family`. The email comes from the verified Firebase token. */
export interface CreateTravelBallFamilySignupRequest {
  familyRole: TravelBallFamilyRole;
  firstName: string;
  lastName: string;
  /** They checked "I agree to the Terms of Service and Privacy Policy". The API refuses anything else. */
  acceptedTerms: true;
  acceptedPrivacy: true;
}

export interface TravelBallFamilySignupResponse {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  familyRole: TravelBallFamilyRole;
  /**
   * `created` for a new account, `existing` for an idempotent repeat, `role_added` when a coach
   * account took the parent/guardian role.
   */
  status: 'created' | 'existing' | 'role_added';
}

export interface TravelBallFamilyCoach {
  firstName: string;
  lastName: string;
}

export interface TravelBallFamilyTeam {
  teamId: string;
  teamName: string;
  /** The team's program. Null only on an unavailable card (`isAvailable: false`), which shows no program. */
  organizationName: string | null;
  sport?: string | null;
  playerLevel?: string | null;
  coaches: TravelBallFamilyCoach[];
  /** False while the team's program is paused or deleted: shown as "Team temporarily unavailable". */
  isAvailable: boolean;
}

/** Where a join request stands. `cancelled` requests never reach the home. */
export type TravelBallJoinRequestStatus = 'pending' | 'approved' | 'declined' | 'cancelled';

export interface TravelBallFamilyJoinRequest {
  id: string;
  teamId: string;
  teamName: string;
  /** The team's program. */
  organizationName?: string;
  playerUserId: string;
  status: TravelBallJoinRequestStatus | string;
  createdAt: string;
  decidedAt?: string | null;
  /** Set on a declined request: when the same team can be asked again (24 hours after the decline). */
  canRequestAgainAt?: string | null;
  /**
   * Who sent it: the player, or a linked parent/guardian on their behalf. A player is told when a
   * request was not their own ("Requested by your parent/guardian"). Older answers leave it out.
   */
  requestedByUserId?: string | null;
}

/** A guardian's linked player. */
export interface TravelBallFamilyPlayer {
  /** Null while a coach-invited link waits for the player to claim their roster row. */
  playerUserId?: string | null;
  linkId: string;
  linkStatus: string;
  relationship?: string | null;
  firstName: string;
  lastName: string;
  /** The linked player's account email. Null while `awaiting_player`; older answers leave it out. */
  email?: string | null;
  /** The team a coach invited them onto, while the link is `awaiting_player`. Older answers leave it out. */
  awaitingTeamName?: string | null;
  /**
   * The team this player was on last season, when they have no active-season team (rule 13: families
   * re-join each season). Older answers leave it out, which reads as null.
   */
  previousSeasonTeamName?: string | null;
  teams: TravelBallFamilyTeam[];
  joinRequests: TravelBallFamilyJoinRequest[];
}

/** `GET /api/travel-ball/family/home`. */
export interface TravelBallFamilyHome {
  familyRole: TravelBallFamilyRole;
  firstName: string;
  lastName: string;
  /** A player's own teams. Empty for a guardian. */
  teams: TravelBallFamilyTeam[];
  /** A guardian's linked players. Empty for a player. */
  players: TravelBallFamilyPlayer[];
  /** A player's join requests: every pending one, and those decided in the last 14 days. */
  joinRequests: TravelBallFamilyJoinRequest[];
  /**
   * A player's team last season, when they have no active-season team (rule 13). Null for a guardian
   * (each linked player carries their own). Older answers leave it out, which reads as null.
   */
  previousSeasonTeamName?: string | null;
  /**
   * Link requests waiting for this account to answer, the same as `GET links` `incoming` (at most
   * 50). Older answers leave it out, which reads as empty.
   */
  incomingLinkRequests: TravelBallFamilyIncomingLink[];
  /**
   * Coaches' roster invites to this account's own address, the kind matching the family role, still
   * pending (#15; newest first, at most 20). Never the token: Accept and Decline go by `id`. Older
   * answers leave it out, which reads as empty.
   */
  rosterInvites: TravelBallFamilyRosterInvite[];
}

/** A coach's roster invite shown on the family Home (#15). */
export interface TravelBallFamilyRosterInvite {
  id: string;
  kind: 'player' | 'guardian' | string;
  teamId: string;
  teamName: string;
  /** The team's program. */
  organizationName: string;
  /** The roster spot's first name: whose parent/guardian a guardian invite is for. */
  playerFirstName: string;
  sentAt: string;
  expiresAt: string;
}

/** `POST /api/travel-ball/family/roster-invites/{id}/accept`. The boxes matter only while legal is pending. */
export interface AcceptTravelBallFamilyRosterInviteRequest {
  acceptedTerms?: boolean;
  acceptedPrivacy?: boolean;
  platform?: string;
}

/** One `GET /api/travel-ball/family/team-search` result: names only. */
export interface TravelBallFamilyTeamSearchItem {
  teamId: string;
  teamName: string;
  /** The team's program. */
  orgName: string;
  sport: string;
  playerLevel?: string | null;
}

export interface TravelBallFamilyTeamSearchPage {
  items: TravelBallFamilyTeamSearchItem[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

/** A teammate as a family sees them: no contact details. */
export interface TravelBallFamilyRosterPlayer {
  id: string;
  firstName: string;
  lastName: string;
  jerseyNumber?: string | null;
  position?: string | null;
  secondaryPosition?: string | null;
  /** True on the signed-in player's own row. */
  isMe: boolean;
  /**
   * A guardian's view: true on rows held by their OTHER linked players. The API lists the viewed
   * player first, then these, then everyone else. Older answers leave it out.
   */
  isLinkedPlayer?: boolean;
}

/** The signed-in player's own roster row, read-only: the coach owns it. */
export interface TravelBallFamilyOwnPlayer {
  id: string;
  firstName: string;
  lastName: string;
  position?: string | null;
  secondaryPosition?: string | null;
  gradYear?: number | null;
  playerLevel?: string | null;
  pitchHand?: string | null;
  batHand?: string | null;
  height?: string | null;
  weight?: string | null;
  jerseyNumber?: string | null;
}

/** `GET /api/travel-ball/family/teams/{teamId}`. 404 `team_not_found` unless the player is on it. */
export interface TravelBallFamilyTeamView {
  teamId: string;
  teamName: string;
  /** The team's program. */
  organizationName: string;
  sport: string;
  playerLevel?: string | null;
  coaches: TravelBallFamilyCoach[];
  roster: TravelBallFamilyRosterPlayer[];
  me: TravelBallFamilyOwnPlayer;
  /** Up to 3 newest active announcements on this team for the reader; reading the view marks none read. */
  recentAnnouncements: FamilyAnnouncement[];
  /** The reader is an announcer on this team right now, so they can post to it. */
  canAnnounce: boolean;
}

/** How a parent/guardian is related to a linked player. The UI says Mother, Father, Guardian, Other. */
export type TravelBallGuardianRelationship = 'mother' | 'father' | 'guardian' | 'other';

export const GUARDIAN_RELATIONSHIPS: readonly TravelBallGuardianRelationship[] = [
  'mother',
  'father',
  'guardian',
  'other',
];

export const isGuardianRelationship = (value: unknown): value is TravelBallGuardianRelationship =>
  typeof value === 'string' && (GUARDIAN_RELATIONSHIPS as readonly string[]).includes(value);

/** An accepted link, from either side: the other person's name. */
export interface TravelBallFamilyLinkedPerson {
  linkId: string;
  userId: string;
  firstName: string;
  lastName: string;
  /** The other account's email: names are typed at signup, the email says who it really is. */
  email?: string | null;
  relationship?: string | null;
  status: 'accepted' | string;
}

/** A link request someone sent this account, waiting for Accept or Decline. */
export interface TravelBallFamilyIncomingLink {
  linkId: string;
  firstName: string;
  lastName: string;
  /** The requester's account email, so the recipient can tell who is really asking. */
  email?: string | null;
  relationship?: string | null;
  requestedAt: string;
}

/**
 * A link request this account sent. Only the typed email: whether an account exists behind it is
 * never shown, so nobody can probe for accounts. A link made by accepting a coach's roster invite has
 * no typed email (`email: null`) and names the player and team instead.
 */
export interface TravelBallFamilyOutgoingLink {
  linkId: string;
  email: string | null;
  /** Coach-invite links: the roster row's first name. */
  playerFirstName?: string | null;
  /** Coach-invite links: the team the invite was for. */
  teamName?: string | null;
  relationship?: string | null;
  requestedAt: string;
}

/** `GET /api/travel-ball/family/links`: at most 50 of each. */
export interface TravelBallFamilyLinks {
  linked: TravelBallFamilyLinkedPerson[];
  incoming: TravelBallFamilyIncomingLink[];
  outgoing: TravelBallFamilyOutgoingLink[];
}

/** `POST /api/travel-ball/family/links`. A guardian must say how they are related; a player sends none. */
export interface CreateTravelBallFamilyLinkRequest {
  email: string;
  relationship?: TravelBallGuardianRelationship;
}

/** `POST /api/travel-ball/family/links` always answers 202 with the same message. */
export interface TravelBallFamilyLinkRequestSent {
  message: string;
}

/** `GET /api/public/travel-ball/availability`. */
export interface TravelBallAvailability {
  enabled: boolean;
}
